import {
	AfterViewInit,
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import * as echarts from 'echarts';
import _ from 'lodash';


// CC: exported so consumers can supply their own node config via the nodeCfg Input
export type NodeCfg = Record<string, {
	color: string;
	label: string;
	symbol: string;
	icon: string;
	sizeMultiplier: number;
}>;

const DEFAULT_NODE_CFG: NodeCfg = {
	green: {
		color: '#2ecc71', label: 'User', symbol: 'circle', icon: '\uf007',
		sizeMultiplier: 1,
	},
	blue: {
		color: '#4d8af0', label: 'Channel', symbol: 'circle', icon: '\uf292',
		sizeMultiplier: 1.3,
	},
	red: {
		color: '#e05252', label: 'Chat', symbol: 'circle', icon: '\uf075',
		sizeMultiplier: 1.3,
	},
	purple: {
		color: '#9b59b6', label: 'Meeting', symbol: 'circle', icon: '\uf03d',
		sizeMultiplier: 1.3,
	},
	orange: {
		color: '#e8a838', label: 'Drive', symbol: 'circle', icon: '\uf0c2',
		sizeMultiplier: 1.3,
	},
};

@Component({
	selector: 'echarts-graph',
	template: `<div class="f-1-0 w100 h100" #chartEl></div>`,
})
export class EchartsGraphComponent implements AfterViewInit, OnChanges, OnDestroy {

	@Input() rawGraphData: RawGraphData;
	@Input() focusedNodesNames: string[] | null = null;
	@Input() nodeOptionsFn: (node: any, allLinks: any[]) => void;
	@Input() showLegend = false;
	@Input() nodeCfg: NodeCfg = DEFAULT_NODE_CFG;
	// CC: when true, source (sender) nodes show only the local part instead of the full email
	@Input() abbreviateSourceLabel = false;

	// CC: fallback derives from the (possibly overridden) nodeCfg Input
	private get fallbackCfg() {
		return this.nodeCfg['blue'] || Object.values(this.nodeCfg)[0];
	}

	@ViewChild('chartEl') chartEl: ElementRef<HTMLDivElement>;

	private chart: echarts.ECharts | null = null;
	private graphData: PreparedGraph | null = null;
	private resizeCleanup: (() => void) | null = null;

	ngAfterViewInit() {
		this.initChart();
		this.setupResizeObserver();
		if (this.graphData) {
			this.renderChart();
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.rawGraphData && this.rawGraphData) {
			// shallow-copy links instead of deep-clone — links are simple {source, target} objects
			const shallowCopy: RawGraphData = {
				links: this.rawGraphData.links?.map(l => ({...l})),
				nodes: this.rawGraphData.nodes ? [...this.rawGraphData.nodes] : undefined,
			};
			this.graphData = this.buildGraphData(shallowCopy);
			if (this.chart) {
				this.renderChart();
			}
		}
		if (changes.focusedNodesNames && !changes.rawGraphData && this.chart) {
			this.applySearchFocus();
		}
	}

	ngOnDestroy() {
		this.resizeCleanup?.();
		this.chart?.dispose();
		this.chart = null;
	}

	private initChart() {
		const el = this.chartEl?.nativeElement;
		if (!el) return;
		this.chart = echarts.init(el, null, { renderer: 'canvas' });
	}

	private setupResizeObserver() {
		const el = this.chartEl?.nativeElement;
		if (!el || !this.chart) return;

		const onResize = _.debounce(() => {
			this.chart?.resize();
		}, 150);

		const ro = new ResizeObserver(onResize);
		ro.observe(el);
		this.resizeCleanup = () => { ro.disconnect(); onResize.cancel(); };
	}

	private buildGraphData(rawData: RawGraphData): PreparedGraph {
		const links = rawData.links || [];

		// deduplicate links with O(n) Set instead of O(n²) _.uniqWith
		const linkKeySet = new Set<string>();
		const uniqueLinks = [];
		for (const l of links) {
			const key = l.source + '>' + l.target;
			if (!linkKeySet.has(key)) {
				linkKeySet.add(key);
				uniqueLinks.push(l);
			}
		}

		const nodeIds = _.union(rawData.nodes || [], _.flatMap(uniqueLinks, l => [l.source, l.target]));

		// build adjacency + source set in one pass instead of O(nodes × links) .some()
		const adjacency = new Map<string, Set<string>>();
		const sourceIds = new Set<string>();
		nodeIds.forEach(id => adjacency.set(id, new Set()));
		for (const l of uniqueLinks) {
			adjacency.get(l.source)?.add(l.target);
			adjacency.get(l.target)?.add(l.source);
			sourceIds.add(l.source);
		}

		const nodes = nodeIds.map(id => {
			const node: any = {
				id,
				name: id,
				isSource: sourceIds.has(id),
				connectedNodeIds: [...(adjacency.get(id) || [])],
				color: 'blue',
			};
			this.nodeOptionsFn?.(node, uniqueLinks);
			return node;
		});

		return { nodes, links: uniqueLinks };
	}

	private renderChart() {
		if (!this.chart || !this.graphData) return;

		if (!this.graphData.nodes.length) {
			this.chart.clear();
			return;
		}

		this.chart.setOption(this.buildOption(), { notMerge: true });
	}

	private buildOption(): echarts.EChartsOption {
		const { nodes, links } = this.graphData!;

		// for large graphs, disable layout animation and reduce simulation cost
		const isLargeGraph = links.length > 1500;

		const presentLabels = new Set(nodes.map(n => (this.nodeCfg[n.color] || this.fallbackCfg).label));
		const legendItems = Object.values(this.nodeCfg)
			.filter(cfg => presentLabels.has(cfg.label))
			.map(cfg => ({ name: cfg.label, icon: cfg.symbol, itemStyle: { color: cfg.color } }));

		const ecNodes = nodes.map(n => this.buildNodeData(n, isLargeGraph));
		const ecLinks = links.map(l => ({ source: l.source, target: l.target }));

		const n = nodes.length;
		const initialZoom = n <= 15 ? 2.2 : n <= 40 ? 1.9 : n <= 80 ? 1.65 : 1.45;

		return {
			backgroundColor: 'transparent',
			legend: this.showLegend ? [{
				show: true,
				selectedMode: true,
				orient: 'vertical',
				right: 16,
				top: 'middle',
				data: legendItems,
				itemWidth: 16,
				itemHeight: 16,
				itemGap: 12,
				backgroundColor: 'rgba(255,255,255,0.05)',
				borderRadius: 8,
				padding: [10, 14],
				textStyle: { color: '#8892a4', fontSize: 12 },
			}] : [],
			tooltip: {
				trigger: 'item',
				enterable: false,
				backgroundColor: 'rgba(15,19,36,0.97)',
				borderColor: 'rgba(77,138,240,0.35)',
				textStyle: { color: '#c8d4e8', fontSize: 12, lineHeight: 20 },
				padding: [10, 14],
				extraCssText: 'border-radius: 8px;',
			},
			series: [{
				type: 'graph',
				layout: 'force',
				animation: false,
				animationDuration: 0,
				animationEasing: 'linear',
				roam: true,
				draggable: !isLargeGraph,
				zoom: initialZoom,
				center: ['50%', '50%'],
				large: isLargeGraph,
				categories: Object.values(this.nodeCfg)
					.filter(cfg => presentLabels.has(cfg.label))
					.map(cfg => ({ name: cfg.label, itemStyle: { color: cfg.color } })),
				data: ecNodes,
				links: ecLinks,
				force: {
					initLayout: 'circular',
					repulsion: isLargeGraph ? 50 : 1050,
					edgeLength: isLargeGraph ? [10, 30] : [60, 160],
					gravity: isLargeGraph ? 0.9 : 0.7,
					friction: isLargeGraph ? 0.95 : 0.8,
					layoutAnimation: false,
				},
				labelLayout: { hideOverlap: isLargeGraph },
				lineStyle: {
					color: '#cccccc',
					curveness: 0.15,
					opacity: isLargeGraph ? 0.15 : 0.6,
					width: isLargeGraph ? 0.5 : 2,
				},
				emphasis: {
					focus: 'adjacency',
					lineStyle: { width: 2.5, opacity: 1 },
					itemStyle: { borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' },
				},
				blur: {
					itemStyle: { opacity: 0.08 },
					lineStyle: { opacity: 0.04 },
					label: { opacity: 0.08 },
				},
			} as any],
		};
	}

	private applySearchFocus() {
		if (!this.chart || !this.graphData) return;

		if (!this.focusedNodesNames?.length) {
			this.chart.dispatchAction({ type: 'downplay', seriesIndex: 0 });
			return;
		}

		const matchedIndices = this.graphData.nodes
			.map((n, i) => ({ n, i }))
			.filter(({ n }) => this.focusedNodesNames!.includes(n.name))
			.map(({ i }) => i);

		this.chart.dispatchAction({ type: 'downplay', seriesIndex: 0 });
		matchedIndices.forEach(dataIndex => {
			this.chart?.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex });
		});
	}

	private buildNodeData(node: any, isLargeGraph = false): any {
		const cfg = this.nodeCfg[node.color] || this.fallbackCfg;
		const connections = node.connectedNodeIds.length;
		const size = isLargeGraph ? 8 : 20;
		const symbolSize = [size * cfg.sizeMultiplier, size * cfg.sizeMultiplier];
		const tooltipExtra = node.tooltipExtra ? `<br/>${node.tooltipExtra}` : '';

		// for large graphs, use simple flat color and hide labels to avoid rendering 100K+ text elements
		if (isLargeGraph) {
			return {
				id: node.id,
				name: node.name,
				category: cfg.label,
				symbol: cfg.symbol,
				symbolSize,
				itemStyle: {
					color: cfg.color,
					borderWidth: 0,
				},
				label: {
					show: false
				},
				tooltip: {
					formatter: `<b>${node.name}</b><br/>Type: ${cfg.label}<br/>Connections: ${connections}${tooltipExtra}`,
				},
			};
		}

		const rawLabel = node.isSource && this.abbreviateSourceLabel ? this.abbreviateEmail(node.name) : node.name;

		return {
			id: node.id,
			name: node.name,
			category: cfg.label,
			symbol: cfg.symbol,
			symbolSize,
			itemStyle: {
				color: {
					type: 'linear',
					x: 0, y: 0, x2: 0, y2: 1,
					colorStops: [
						{ offset: 0, color: this.lightenHex(cfg.color, 0.42) + 'dd' },
						{ offset: 1, color: this.lightenHex(cfg.color, 0.12) + 'aa' },
					],
					global: false,
				},
				borderColor: this.lightenHex(cfg.color, 0.1),
				borderWidth: 2,
			},
			label: {
				show: true,
				// text above, icon overlapping/inside the node
				position: 'inside' as const,
				offset: [0, -symbolSize * 0.42],
				align: 'center' as const,
				verticalAlign: 'middle' as const,
				formatter: `{t|${rawLabel}}\n{i|${cfg.icon}}`,
				rich: {
					t: {
						fontSize: 12,
						fontWeight: '500',
						color: '#111111',
						lineHeight: 16,
						padding: [0, 6, 35, 6],
						borderRadius: 10,
					},
					i: {
						fontFamily: '"Font Awesome 7 Pro"',
						fontWeight: '900',
						fontSize: Math.max(16, Math.round(15 * 0.34)),
						color: cfg.color,
						lineHeight: Math.max(18, Math.round(15 * 0.34)),
						padding: [0, 0, 18, 0],
						align: 'center',
					},
				},
			},
			tooltip: {
				formatter: `<b>${node.name}</b><br/>Type: ${cfg.label}<br/>Connections: ${connections}${tooltipExtra}`,
			},
		};
	}

	private abbreviateEmail(email: string): string {
		if (!email?.includes('@')) return email;
		return email.split('@')[0];
	}

	private lightenHex(hex: string, amount: number): string {
		const n = parseInt(hex.replace('#', ''), 16);
		const r = Math.min(255, (n >> 16) + Math.round(255 * amount));
		const g = Math.min(255, ((n >> 8) & 0xff) + Math.round(255 * amount));
		const b = Math.min(255, (n & 0xff) + Math.round(255 * amount));
		return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
	}
}

type RawGraphData = { links?: { source: string; target: string }[]; nodes?: string[] };
type PreparedGraph = { nodes: any[]; links: { source: string; target: string }[] };
